import React, { useState, useEffect, useCallback } from 'react';
import { Collapse } from '@mui/material';

import { IStore } from '@ocx-app/modules/stores/types';

import { StoreCard } from './store-card/store-card.component';

// Allow correct "flex stack" calculation inside the Collapse
const collapseCss = {
  display: 'flex',
  '& .MuiCollapse-wrapperInner': { display: 'flex', flexDirection: 'column' },
};

/*
The App follows the unidirectional data flow.
So we need to use memoization for IStore data (inside component) when user un-select store preview
and apply animation for already un-selected preview
*/

type StorePreviewProps = {
  store?: IStore | null;
  delay?: number; // wait ms before close animation start
  onStoreClick(storeId: string): void;
  showDirection?: boolean;
};

export const StorePreview: React.FC<StorePreviewProps> = (props) => {
  const { store = null, onStoreClick, delay = 0, showDirection = false } = props;
  const [viewStore, setViewStore] = useState<IStore | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const exec = async () => {
      if (store !== null) {
        setViewStore(store);
      } else {
        // Wait till parent animation finished
        await new Promise((res) => {
          setTimeout(res, delay);
        });
        setOpen(false);
      }
    };
    exec();
  }, [delay, store]);

  useEffect(() => {
    if (viewStore !== null) {
      setOpen(true);
    }
  }, [viewStore]);

  const onExited = useCallback(() => setViewStore(null), []);
  const onCardClick = useCallback(() => viewStore && onStoreClick(viewStore.id), [onStoreClick, viewStore]);

  return (
    <Collapse in={open} timeout="auto" unmountOnExit onExited={onExited} sx={collapseCss}>
      {viewStore !== null && (
        <StoreCard
          addressLine1={viewStore.addressLine1}
          addressLine2={viewStore.addressLine2}
          distance={showDirection ? viewStore.distance : null}
          priceGroups={viewStore.priceGroups}
          storeId={viewStore.id}
          onCardClick={onCardClick}
          directionLink={showDirection ? viewStore.directionLink : null}
        />
      )}
    </Collapse>
  );
};
